import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useKeenSlider } from 'keen-slider/react';
import { useIsomorphicLayoutEffect } from '@hub/hooks/useIsomorphicLayoutEffect';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import type { WidenMediaElement } from '@hub/libs/widen-utils';

export interface ProductMediaSwipeCarouselProps {
  activeIndex: number;
  media: WidenMediaElement[];
  onChange: (idx: number) => void;
  onClick: (idx: number) => void;
}

export const ProductMediaSwipeCarousel = ({
  activeIndex,
  media,
  onChange,
  onClick,
}: ProductMediaSwipeCarouselProps) => {
  const [dragging, setDraggging] = useState(false);
  const isMountedRef = useRef<boolean>(false);
  const { t } = useTranslation('product/productMediaCarousel');
  const slidesPerView = media.length > 2 ? 1.5 : 1;

  const [carouselRef, carousel] = useKeenSlider<HTMLDivElement>({
    initial: activeIndex,
    loop: true,
    spacing: 20,
    centered: true,
    slidesPerView,
    slides: '.keen-slider__slide',
    dragStart: () => {
      /* istanbul ignore if */
      if (!isMountedRef.current) {
        return;
      }
      setDraggging(true);
    },
    dragEnd: s => {
      /* istanbul ignore if */
      if (isMountedRef.current && Math.abs(s.details().speed) < 1) {
        setDraggging(false);
      }
    },
    afterChange: s => {
      if (isMountedRef.current && Math.abs(s.details().speed) < 1) {
        setDraggging(false);
      }
    },
    slideChanged: s => {
      if (!isMountedRef.current || s.details().relativeSlide === activeIndex) {
        return;
      }
      onChange(s.details().relativeSlide);
    },
  });

  useEffect(() => {
    if (carousel && carousel.details().relativeSlide !== activeIndex) {
      carousel.moveToSlideRelative(activeIndex, true);
    }
  }, [activeIndex, carousel]);

  useEffect(() => {
    if (carousel) {
      carousel.refresh();
    }
  }, [media, carousel]);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <>
      <div
        data-testid="product-image-carousel-swipe"
        ref={carouselRef}
        className="relative keen-slider"
        style={{ width: 220 * slidesPerView, height: 165 }}
      >
        {media.map((img, idx) => (
          <div
            className={`cursor-pointer relative keen-slider__slide number-slide${idx + 1}`}
            key={`fade-carousel-image-${img.shortCode}`}
            onClick={() => !dragging && onClick(idx)}
          >
            <WidenImage
              width={220}
              height={165}
              alt={img.description || img.title}
              layout="fixed"
              shortCode={img.shortCode}
              filename={img.filename}
              title={img.title}
              priority={img.priority}
            />
          </div>
        ))}
      </div>
      <div className="text-center text-xs my-3">
        {t('PRODUCT_MEDIA_CAROUSEL.CURRENT_INDEX', {
          active: activeIndex + 1,
          total: media.length,
          ns: 'bearing-hub/bearingHub',
        })}
      </div>
    </>
  );
};
